import { useState, useEffect, useCallback } from 'react';
import '@/styles/forms.css';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { CheckCircle, X, Palette, Calendar, ArrowRight, Loader2, ShoppingCart } from 'lucide-react';
import { PLAN_PRICES, ADDON_PRICES } from '../config/pricing';
import { discountedUsd, linePrices, fmtUsd } from '../lib/checkoutLink';
import { track } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const ADDON_DISPLAY = [
  {
    key: 'white_label',
    Icon: Palette,
    iconColor: 'var(--accent-text)',
    iconBg: 'var(--accent-tint)',
    label: 'White Label Report',
    desc: 'Remove UXAuditPro branding — deliver reports under your own brand',
  },
  {
    key: 'expert_call',
    Icon: Calendar,
    iconColor: 'var(--accent-text)',
    iconBg: 'var(--accent-tint)',
    label: '30-Min Expert Call',
    desc: 'Personal walkthrough of your audit results with a UX specialist',
  },
];

const ADDON_CONFIG = ADDON_DISPLAY.map((a) => ({ ...a, price: ADDON_PRICES[a.key].usd }));
// Indian add-ons (INR, inclusive of all taxes)
const ADDON_CONFIG_INR = ADDON_DISPLAY.map((a) => ({ ...a, price: ADDON_PRICES[a.key].inr }));

const BASE_PRICE_USD = PLAN_PRICES.STARTER.usd;
// INR price includes 18% GST for Indian users
const BASE_PRICE_INR = PLAN_PRICES.STARTER.inr;

// One order line's price; struck through with the new price beside it when the
// promo changed it (owner, 2026-10-09: "Audit $49 → $0, Expert call $99").
function LineAmount({ line, before, after, inr }) {
  const fmt = (v) => (inr ? `₹${Math.round(v)}` : `$${fmtUsd(v)}`);
  return (
    <span className="text-sm font-medium" data-testid={`order-line-${line}`} style={{ color: 'var(--ink-1)' }}>
      {after !== before && (
        <>
          <span className="sr-only">was </span>
          <s style={{ color: 'var(--ink-2)' }}>{fmt(before)}</s>
          <span aria-hidden="true"> → </span>
          <span className="sr-only">, now </span>
        </>
      )}
      {fmt(after)}
    </span>
  );
}

/** Dynamically load the Razorpay checkout script (idempotent). */
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// `source` labels where the checkout was opened from, for the
// upgrade_clicked analytics event (e.g. quota_modal, report_blur,
// pricing_page). Purely informational — no behavior depends on it.
// auditedUrl / auditedFile: the page, or the uploaded file's name, kept on the
// order so a recovery email can name it. initialPromo: a code to apply on open
// (the recovery email's own). initialStep: 'checkout' opens on the order
// summary, one click from the pay button. reuploadFile: a resumed file audit,
// whose file we never kept, so the buyer is told to upload it again.
export default function AuditCheckoutPopup({ visible, onClose, user, setUser, source = 'unknown', initialAddOns = null, auditedUrl = null, auditedFile = null, initialPromo = null, initialStep = 'addons', reuploadFile = null }) {
  const [step, setStep] = useState('addons'); // 'addons' | 'checkout'
  const [addOns, setAddOns] = useState({ white_label: false, expert_call: false });
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState('');
  const [gateway, setGateway] = useState(null); // null | 'paypal' | 'razorpay'
  const [gatewayLoading, setGatewayLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [promoScope, setPromoScope] = useState('order'); // validate-promo's applies_to
  const [appliedPromo, setAppliedPromo] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);

  const totalUsd =
    BASE_PRICE_USD +
    (addOns.white_label ? ADDON_PRICES.white_label.usd : 0) +
    (addOns.expert_call ? ADDON_PRICES.expert_call.usd : 0);

  const totalInr =
    BASE_PRICE_INR +
    (addOns.white_label ? ADDON_PRICES.white_label.inr : 0) +
    (addOns.expert_call ? ADDON_PRICES.expert_call.inr : 0);

  // The discount covers the whole order, or the audit only (promoScope), each
  // item to the cent, exactly as PayPal is charged (backend pricing.usd_lines).
  const lineKeys = ['plan'].concat(['white_label', 'expert_call'].filter((k) => addOns[k]));
  const usdPrices = lineKeys.map((k) => (k === 'plan' ? BASE_PRICE_USD : ADDON_PRICES[k].usd));
  const inrPrices = lineKeys.map((k) => (k === 'plan' ? BASE_PRICE_INR : ADDON_PRICES[k].inr));
  const lineFor = Object.fromEntries(lineKeys.map((k, i) => [k, {
    usd: linePrices(usdPrices, discountPercent, promoScope)[i],
    inr: linePrices(inrPrices, discountPercent, promoScope)[i],
  }]));
  const discountedTotalUsd = discountedUsd(usdPrices, discountPercent, promoScope);
  const priceOf = (k) => {
    const [before, after] = lineFor[k][gateway === 'razorpay' ? 'inr' : 'usd'];
    return { before, after };
  };
  const discountedTotalInr = discountPercent > 0
    ? (promoScope === 'audit'
      ? Math.round(BASE_PRICE_INR * (1 - discountPercent / 100)) + totalInr - BASE_PRICE_INR
      : Math.round(totalInr * (1 - discountPercent / 100)))
    : totalInr;

  // Always start at step 1 when popup opens
  useEffect(() => {
    if (visible) {
      setStep(initialStep || 'addons');
      if (initialAddOns) setAddOns(initialAddOns);
      setError('');
      setPromoCode('');
      setPromoError('');
      setDiscountPercent(0);
      setAppliedPromo('');
      if (initialPromo) handleApplyPromo(initialPromo);
    }
  }, [visible, initialAddOns, initialPromo, initialStep]);

  const resetAndClose = () => {
    setStep('addons');
    setAddOns({ white_label: false, expert_call: false });
    setError('');
    setLoading(false);
    setGateway(null);
    setPromoCode('');
    setPromoError('');
    setDiscountPercent(0);
    setAppliedPromo('');
    onClose();
  };

  const toggleAddon = (key) =>
    setAddOns((prev) => ({ ...prev, [key]: !prev[key] }));

  // Detect gateway when popup opens
  const detectGateway = useCallback(async () => {
    if (!visible) return;
    setGatewayLoading(true);
    try {
      const resp = await axios.post(`${API}/payments/detect-gateway`, {}, { withCredentials: true });
      setGateway(resp.data.gateway || 'paypal');
    } catch {
      setGateway('paypal'); // fallback
    } finally {
      setGatewayLoading(false);
    }
  }, [visible]);

  useEffect(() => {
    if (visible && !gateway) {
      detectGateway();
    }
  }, [visible, gateway, detectGateway]);

  // Reset gateway when popup closes so it re-detects on next open
  useEffect(() => {
    if (!visible) setGateway(null);
  }, [visible]);

  const handleApplyPromo = async (given) => {
    const code = (typeof given === 'string' ? given : promoCode).trim().toUpperCase();
    if (!code) return;
    if (typeof given === 'string') setPromoCode(code);
    setApplyingPromo(true);
    setPromoError('');
    try {
      const resp = await axios.post(
        `${API}/payments/validate-promo`,
        { code, plan: 'STARTER' },
        { withCredentials: true }
      );
      if (resp.data.valid) {
        setDiscountPercent(resp.data.discount_percent);
        setPromoScope(resp.data.applies_to || 'order');
        setAppliedPromo(code);
        setPromoError('');
      } else {
        setPromoError('Invalid or expired promo code.');
        setDiscountPercent(0);
        setAppliedPromo('');
      }
    } catch (e) {
      setPromoError(e.response?.data?.detail || 'Invalid or expired promo code.');
      setDiscountPercent(0);
      setAppliedPromo('');
    } finally {
      setApplyingPromo(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setAuthLoading(true);
    setError('');
    try {
      const resp = await axios.post(
        `${API}/auth/google/token`,
        { credential: credentialResponse.credential },
        { withCredentials: true }
      );
      if (resp.data.success) {
        setUser(resp.data.user);
      }
    } catch {
      setError('Sign-in failed. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  // ── PayPal flow ──
  const handlePayPal = async () => {
    track('upgrade_clicked', { source, gateway: 'paypal', plan: 'STARTER' });
    setLoading(true);
    setError('');
    try {
      const resp = await axios.post(
        `${API}/payments/create-order`,
        { plan: 'STARTER', origin_url: window.location.origin, add_ons: addOns, ...(appliedPromo && { promo_code: appliedPromo }), ...(auditedUrl && { audited_url: auditedUrl }), ...(auditedFile && { audited_file: auditedFile }) },
        { withCredentials: true }
      );
      if (resp.data.free) {
        // 100% discount — subscription activated, no payment needed
        const promoId = resp.data.payment_id || '';
        window.location.href = `${window.location.origin}/thank-you?payment=success&plan=STARTER&gateway=promo${promoId ? `&paymentId=${promoId}` : ''}`;
      } else if (resp.data.approval_url) {
        window.location.href = resp.data.approval_url;
      } else {
        setError('Could not initialize payment. Please try again.');
        setLoading(false);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Payment failed. Please try again.');
      setLoading(false);
    }
  };

  // ── Razorpay flow ──
  const handleRazorpay = async () => {
    track('upgrade_clicked', { source, gateway: 'razorpay', plan: 'STARTER' });
    setLoading(true);
    setError('');
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        setError('Could not load payment provider. Please check your connection and try again.');
        setLoading(false);
        return;
      }

      const resp = await axios.post(
        `${API}/payments/razorpay/create-order`,
        { plan: 'STARTER', add_ons: addOns, ...(appliedPromo && { promo_code: appliedPromo }), ...(auditedUrl && { audited_url: auditedUrl }), ...(auditedFile && { audited_file: auditedFile }) },
        { withCredentials: true }
      );

      // 100% discount — no payment needed, subscription already activated
      if (resp.data.free) {
        const promoId = resp.data.payment_id || '';
        window.location.href = `${window.location.origin}/thank-you?payment=success&plan=STARTER&gateway=promo${promoId ? `&paymentId=${promoId}` : ''}`;
        return;
      }

      const { order_id, amount, currency, key_id } = resp.data;
      // Held for purchase_completed below. Razorpay amounts are in the
      // minor unit (paise) — pricing.py:96 builds the table as inr_paise —
      // so this is /100 to become a currency amount, not a magic number.
      const chargedValue = typeof amount === 'number' ? amount / 100 : null;

      const options = {
        key: key_id,
        amount,
        currency,
        name: 'UXAuditPro',
        description: 'Starter Audit (incl. 18% GST)',
        order_id,
        prefill: {
          email: user?.email || '',
          name: user?.name || '',
        },
        theme: { color: 'var(--accent-text)' },
        handler: async (response) => {
          try {
            await axios.post(
              `${API}/payments/razorpay/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { withCredentials: true }
            );
            // CONFIRMED SUCCESS. The server has checked the signature and
            // marked the order completed (razorpay_routes.py:209-275); this
            // line is only reached when that POST did not throw. Fired here
            // rather than on /thank-you because that page renders its success
            // state for a bare visit with no params (ThankYouPage.js:34-38),
            // which would count a purchase for anyone who types the URL.
            track('purchase_completed', {
              gateway: 'razorpay',
              plan: 'STARTER',
              ...(chargedValue ? { value: chargedValue, currency: currency || 'INR' } : {}),
            });
            window.location.href = `${window.location.origin}/thank-you?payment=success&plan=STARTER&gateway=razorpay`;
          } catch (verifyErr) {
            setError(verifyErr.response?.data?.detail || 'Payment verification failed. Please contact support.');
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => setLoading(false),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        setError(response.error?.description || 'Payment failed. Please try again.');
        setLoading(false);
      });
      rzp.open();
    } catch (e) {
      setError(e.response?.data?.detail || 'Payment failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AnimatePresence>
        {visible && (
          <div
            /* A NAMED HOOK, because a route has to be able to assert this is
               ABSENT. /audit/new declares that it rendered the wizard with no
               paywall over it, and a class-list selector would stop matching
               the first time this is restyled - silently, since `absent`
               passes when nothing matches. That is a guard that cannot fail. */
            data-testid="audit-checkout-modal"
            className="fixed inset-0 flex items-center justify-center z-[60] p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
            onClick={(e) => { if (e.target === e.currentTarget) resetAndClose(); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 14 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="bg-white rounded-2xl w-full max-w-md"
              style={{
                border: '1px solid var(--border-2)',
                boxShadow: '0 24px 80px rgba(0,0,0,0.22)',
                maxHeight: '92vh',
                overflowY: 'auto',
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-6 pb-0">
                <div className="flex items-center gap-3">
                  {step === 'checkout' ? (
                    <button
                      onClick={() => { setStep('addons'); setError(''); }}
                      className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-gray-100 transition-colors"
                      aria-label="Back to add-ons"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="var(--accent-text)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 5l-7 7 7 7" />
                      </svg>
                    </button>
                  ) : (
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: 'var(--accent-tint)' }}
                    >
                      <ShoppingCart className="w-5 h-5" style={{ color: 'var(--accent-text)' }} />
                    </div>
                  )}
                  <div>
                    <p
                      className="font-bold text-sm"
                      style={{ color: 'var(--ink-1)' }}
                    >
                      {step === 'addons' ? 'Enhance Your Audit' : 'Order Summary'}
                    </p>
                    <p
                      className="text-sm"
                      style={{ color: 'var(--ink-2)' }}
                    >
                      Step {step === 'addons' ? '1' : '2'} of 2
                    </p>
                  </div>
                </div>
                <button
                  onClick={resetAndClose}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" style={{ color: 'var(--ink-2)' }} />
                </button>
              </div>

              {/* Progress bar */}
              <div className="px-6 mt-4 mb-5">
                <div className="h-1 rounded-full" style={{ backgroundColor: 'var(--surface-panel)' }}>
                  <div
                    className="h-1 rounded-full transition-all duration-400"
                    style={{
                      backgroundColor: 'var(--accent-text)',
                      width: step === 'addons' ? '50%' : '100%',
                    }}
                  />
                </div>
              </div>

              <div className="px-6 pb-6">
                {/* ── STEP 1: Add-ons ── */}
                {step === 'addons' && (
                  <motion.div
                    key="addons"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                  >
                    {/* Base plan card */}
                    <div
                      className="rounded-xl p-4 mb-4"
                      style={{ backgroundColor: 'var(--ink-strong)' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p
                          className="font-bold text-sm text-white"
                        >
                          Starter Audit
                        </p>
                        <span
                          className="text-sm font-bold"
                          style={{ color: 'var(--accent-text)' }}
                        >
                          {gateway === 'razorpay' ? `₹${BASE_PRICE_INR}` : `$${BASE_PRICE_USD}`}
                          {gateway === 'razorpay' && (
                            <span className="text-sm font-normal ml-1" style={{ color: 'var(--accent-text)' }}>
                              incl. GST
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                        {['Full detailed report', 'PDF download', 'Fix recommendations', 'Severity prioritization'].map((f) => (
                          <div key={f} className="flex items-center gap-1.5">
                            <CheckCircle
                              className="w-3 h-3 flex-shrink-0"
                              style={{ color: 'rgba(255,255,255,0.45)' }}
                            />
                            <span
                              className="text-sm"
                              style={{ color: 'rgba(255,255,255,0.7)' }}
                            >
                              {f}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Add-ons */}
                    {(() => {
                      const addonList = gateway === 'razorpay' ? ADDON_CONFIG_INR : ADDON_CONFIG;
                      return (
                        <>
                          <p
                            className="text-sm font-bold uppercase tracking-widest mb-3"
                            style={{ color: 'var(--ink-2)' }}
                          >
                            Optional Add-ons
                          </p>
                          <div className="space-y-3 mb-5">
                            {addonList.map(({ key, Icon, iconColor, iconBg, label, desc, price }) => (
                              <button
                                key={key}
                                onClick={() => toggleAddon(key)}
                                className="w-full text-left rounded-xl p-4 transition-all"
                                style={{
                                  border: addOns[key] ? '2px solid var(--accent)' : '1px solid var(--border-2)',
                                  backgroundColor: addOns[key] ? 'var(--accent-tint)' : 'var(--surface-canvas)',
                                }}
                                data-testid={`addon-toggle-${key}`}
                              >
                                <div className="flex items-start gap-3">
                                  <div
                                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                                    style={{ backgroundColor: iconBg }}
                                  >
                                    <Icon className="w-4 h-4" style={{ color: iconColor }} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                      <p
                                        className="font-medium text-sm"
                                        style={{ color: 'var(--ink-1)' }}
                                      >
                                        {label}
                                      </p>
                                      <span
                                        className="text-sm font-bold flex-shrink-0"
                                        style={{
                                          color: addOns[key] ? 'var(--accent-text)' : 'var(--ink-1)',
                                        }}
                                      >
                                        {gateway === 'razorpay' ? `+₹${price}` : `+$${price}`}
                                        {gateway === 'razorpay' && (
                                          <span className="text-sm font-normal ml-1" style={{ color: 'var(--ink-2)' }}>incl. tax</span>
                                        )}
                                      </span>
                                    </div>
                                    <p
                                      className="text-sm mt-0.5 leading-relaxed"
                                      style={{ color: 'var(--ink-2)' }}
                                    >
                                      {desc}
                                    </p>
                                  </div>
                                  {/* Checkbox indicator */}
                                  <div
                                    className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                                    style={{
                                      borderColor: addOns[key] ? 'var(--accent)' : 'var(--border-2)',
                                      backgroundColor: addOns[key] ? 'var(--accent-text)' : 'transparent',
                                    }}
                                  >
                                    {addOns[key] && (
                                      <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                                        <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </>
                      );
                    })()}

                    {/* Promo Code — Step 1 */}
                    <div className="mb-4">
                      {appliedPromo ? (
                        <div
                          className="flex items-center justify-between rounded-xl px-4 py-3"
                          style={{ backgroundColor: 'var(--success-bg)', border: '1px solid var(--success)' }}
                        >
                          <div className="flex items-center gap-2">
                            {/* The TICK is green; the SENTENCE is ink.
                                --success on --success-bg measures 4.48:1,
                                which misses AA by 0.02 - and the value it
                                replaced, #16A34A, measured 2.88:1. Rather than
                                invent a darker green, the icon carries the
                                semantic and the text stays readable at
                                16.10:1. Colour is never the only signal. */}
                            <CheckCircle className="w-4 h-4" style={{ color: 'var(--success)' }} />
                            <span
                              className="text-sm font-medium"
                              style={{ color: 'var(--ink-1)' }}
                            >
                              {appliedPromo} — {discountPercent}% off applied
                            </span>
                          </div>
                          <button
                            onClick={() => { setAppliedPromo(''); setDiscountPercent(0); setPromoCode(''); }}
                            className="text-sm hover:opacity-70 transition-opacity"
                            style={{ color: 'var(--ink-2)' }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={promoCode}
                            onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                            onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                            placeholder="Promo code"
                            className="flex-1 px-3 py-2.5 rounded-xl text-sm outline-none"
                            style={{
                              border: promoError ? '1px solid var(--danger)' : '1px solid var(--border-2)',
                              backgroundColor: 'var(--surface-canvas)',
                              color: 'var(--ink-1)',
                            }}
                            data-testid="promo-code-input"
                          />
                          <button
                            onClick={() => handleApplyPromo()}
                            disabled={!promoCode.trim() || applyingPromo}
                            className="uxa-submit uxa-submit--auto uxa-submit--tertiary"
                            data-testid="apply-promo-btn"
                          >
                            {applyingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                              <>Apply<ArrowRight className="w-4 h-4" aria-hidden="true" /></>
                            )}
                          </button>
                        </div>
                      )}
                      {promoError && (
                        <p
                          className="text-sm mt-1.5"
                          style={{ color: 'var(--danger)' }}
                        >
                          {promoError}
                        </p>
                      )}
                    </div>

                    {/* Total */}
                    <div
                      className="flex items-center justify-between py-3 border-t mb-4"
                      style={{ borderColor: 'var(--border-2)' }}
                    >
                      <span
                        className="text-sm"
                        style={{ color: 'var(--ink-2)' }}
                      >
                        Total today
                      </span>
                      <div className="flex items-baseline gap-2">
                        {discountPercent > 0 && (
                          <span
                            className="text-base line-through"
                            style={{ color: 'var(--ink-2)' }}
                          >
                            {gateway === 'razorpay' ? `₹${totalInr}` : `$${totalUsd}`}
                          </span>
                        )}
                        <span
                          className="font-bold text-2xl"
                          style={{
                            color: discountPercent > 0 ? 'var(--success)' : 'var(--ink-1)',
                            letterSpacing: '-0.03em',
                          }}
                        >
                          {gateway === 'razorpay' ? `₹${discountedTotalInr}` : `$${fmtUsd(discountedTotalUsd)}`}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => setStep('checkout')}
                      className="w-full py-3.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
                      style={{
                        backgroundColor: 'var(--accent-text)',
                        color: 'white',
                      }}
                      data-testid="checkout-continue-btn"
                    >
                      Continue to Checkout <ArrowRight className="w-4 h-4" />
                    </button>
                  </motion.div>
                )}

                {/* ── STEP 2: Checkout ── */}
                {step === 'checkout' && (
                  <motion.div
                    key="checkout"
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                  >
                    {reuploadFile && (
                      <p
                        data-testid="reupload-notice"
                        className="rounded-xl p-3 mb-4 text-sm"
                        style={{ backgroundColor: 'var(--surface-panel)', border: '1px solid var(--border-2)', color: 'var(--ink-1)' }}
                      >
                        After you pay, you'll upload <strong>{reuploadFile}</strong> again on this page.
                        We don't keep files from a checkout that didn't finish.
                      </p>
                    )}
                    {/* Order summary */}
                    <div
                      className="rounded-xl p-4 mb-5"
                      style={{ backgroundColor: 'var(--surface-canvas)', border: '1px solid var(--border-2)' }}
                    >
                      <p
                        className="text-sm font-bold uppercase tracking-widest mb-3"
                        style={{ color: 'var(--ink-2)' }}
                      >
                        Your Order
                      </p>
                      <div className="space-y-2.5">
                        <div className="flex justify-between">
                          <span
                            className="text-sm"
                            style={{ color: 'var(--ink-2)' }}
                          >
                            Starter Audit
                          </span>
                          <LineAmount line="plan" inr={gateway === 'razorpay'} {...priceOf('plan')} />
                        </div>
                        {addOns.white_label && (
                          <div className="flex justify-between">
                            <span
                              className="text-sm flex items-center gap-1.5"
                              style={{ color: 'var(--ink-2)' }}
                            >
                              <Palette className="w-3.5 h-3.5" style={{ color: 'var(--accent-text)' }} />
                              White Label Report
                            </span>
                            <LineAmount line="white_label" inr={gateway === 'razorpay'} {...priceOf('white_label')} />
                          </div>
                        )}
                        {addOns.expert_call && (
                          <div className="flex justify-between">
                            <span
                              className="text-sm flex items-center gap-1.5"
                              style={{ color: 'var(--ink-2)' }}
                            >
                              <Calendar className="w-3.5 h-3.5" style={{ color: 'var(--accent-text)' }} />
                              30-Min Expert Call
                            </span>
                            <LineAmount line="expert_call" inr={gateway === 'razorpay'} {...priceOf('expert_call')} />
                          </div>
                        )}
                        {gateway === 'razorpay' && (
                          <div className="flex justify-between text-sm" style={{ color: 'var(--ink-2)' }}>
                            <span>Includes all taxes</span>
                          </div>
                        )}
                        {discountPercent > 0 && (
                          <div className="flex justify-between">
                            <span
                              className="text-sm flex items-center gap-1.5"
                              style={{ color: 'var(--success)' }}
                            >
                              <CheckCircle className="w-3.5 h-3.5" style={{ color: 'var(--success)' }} />
                              Promo: {appliedPromo} ({discountPercent}% off)
                            </span>
                            <span
                              className="text-sm font-medium"
                              style={{ color: 'var(--success)' }}
                            >
                              {gateway === 'razorpay'
                                ? `−₹${totalInr - discountedTotalInr}`
                                : `−$${fmtUsd(totalUsd - discountedTotalUsd)}`}
                            </span>
                          </div>
                        )}
                        <div
                          className="flex justify-between pt-2.5 border-t"
                          style={{ borderColor: 'var(--border-2)' }}
                        >
                          <span
                            className="font-bold text-sm"
                            style={{ color: 'var(--ink-1)' }}
                          >
                            Total
                          </span>
                          <span
                            className="font-bold text-lg"
                            style={{
                              color: discountPercent > 0 ? 'var(--success)' : 'var(--ink-1)',
                              letterSpacing: '-0.03em',
                            }}
                          >
                            {gateway === 'razorpay' ? `₹${discountedTotalInr}` : `$${fmtUsd(discountedTotalUsd)}`}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Promo Code — Step 2 */}
                    <div className="mb-4">
                      {appliedPromo ? (
                        <div
                          className="flex items-center justify-between rounded-xl px-4 py-3"
                          style={{ backgroundColor: 'var(--success-bg)', border: '1px solid var(--success)' }}
                        >
                          <div className="flex items-center gap-2">
                            {/* The TICK is green; the SENTENCE is ink.
                                --success on --success-bg measures 4.48:1,
                                which misses AA by 0.02 - and the value it
                                replaced, #16A34A, measured 2.88:1. Rather than
                                invent a darker green, the icon carries the
                                semantic and the text stays readable at
                                16.10:1. Colour is never the only signal. */}
                            <CheckCircle className="w-4 h-4" style={{ color: 'var(--success)' }} />
                            <span
                              className="text-sm font-medium"
                              style={{ color: 'var(--ink-1)' }}
                            >
                              {appliedPromo} — {discountPercent}% off applied
                            </span>
                          </div>
                          <button
                            onClick={() => { setAppliedPromo(''); setDiscountPercent(0); setPromoCode(''); }}
                            className="text-sm hover:opacity-70 transition-opacity"
                            style={{ color: 'var(--ink-2)' }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={promoCode}
                            onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                            onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                            placeholder="Promo code"
                            className="flex-1 px-3 py-2.5 rounded-xl text-sm outline-none"
                            style={{
                              border: promoError ? '1px solid var(--danger)' : '1px solid var(--border-2)',
                              backgroundColor: 'var(--surface-canvas)',
                              color: 'var(--ink-1)',
                            }}
                            data-testid="promo-code-input"
                          />
                          <button
                            onClick={() => handleApplyPromo()}
                            disabled={!promoCode.trim() || applyingPromo}
                            className="uxa-submit uxa-submit--auto uxa-submit--tertiary"
                            data-testid="apply-promo-btn"
                          >
                            {applyingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                              <>Apply<ArrowRight className="w-4 h-4" aria-hidden="true" /></>
                            )}
                          </button>
                        </div>
                      )}
                      {promoError && (
                        <p
                          className="text-sm mt-1.5"
                          style={{ color: 'var(--danger)' }}
                        >
                          {promoError}
                        </p>
                      )}
                    </div>

                    {/* Error */}
                    {error && (
                      <div
                        className="rounded-xl p-3 mb-4 text-sm"
                        style={{
                          backgroundColor: 'var(--accent-tint)',
                          color: 'var(--danger)',
                          border: '1px solid var(--danger)',
                        }}
                      >
                        {error}
                      </div>
                    )}

                    {!user ? (
                      /* Not logged in — Google sign-in */
                      <div>
                        <p
                          className="text-sm text-center font-medium mb-1"
                          style={{ color: 'var(--ink-1)' }}
                        >
                          Sign in to complete your purchase
                        </p>
                        <p
                          className="text-sm text-center mb-4"
                          style={{ color: 'var(--ink-2)' }}
                        >
                          Your cart is saved. Quick Google sign-in, then pay.
                        </p>
                        {authLoading ? (
                          <div className="flex items-center justify-center gap-2 py-3">
                            <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--accent-text)' }} />
                            <span className="text-sm" style={{ color: 'var(--ink-2)' }}>
                              Signing in…
                            </span>
                          </div>
                        ) : (
                          <div className="flex justify-center">
                            <GoogleLogin
                              onSuccess={handleGoogleSuccess}
                              onError={() => setError('Sign-in failed. Please try again.')}
                              theme="outline"
                              size="large"
                              width="320"
                              text="continue_with"
                              shape="pill"
                              logo_alignment="left"
                              data-testid="checkout-google-login"
                            />
                          </div>
                        )}
                        <p
                          className="text-sm text-center mt-3"
                          style={{ color: 'var(--ink-2)' }}
                        >
                          We only use your account to deliver your audit.
                        </p>
                      </div>
                    ) : gatewayLoading ? (
                      /* Detecting gateway */
                      <div className="flex items-center justify-center gap-2 py-4">
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--accent-text)' }} />
                        <span className="text-sm" style={{ color: 'var(--ink-2)' }}>
                          Preparing checkout…
                        </span>
                      </div>
                    ) : (gateway === 'razorpay' ? discountedTotalInr : discountedTotalUsd) === 0 ? (
                      /* Free checkout — 100% promo, no payment gateway needed */
                      <div>
                        {user.email && (
                          <p
                            className="text-sm text-center mb-4"
                            style={{ color: 'var(--ink-2)' }}
                          >
                            Completing as{' '}
                            <span className="font-medium" style={{ color: 'var(--ink-2)' }}>
                              {user.email}
                            </span>
                          </p>
                        )}
                        <button
                          onClick={gateway === 'razorpay' ? handleRazorpay : handlePayPal}
                          disabled={loading}
                          className="w-full py-3.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-60"
                          /* PROVIDER BRAND FILL, deliberately NOT a token.
                             This is the payment gateway's own button. Repainting
                             it in our palette during the brand pass was wrong:
                             a provider button is recognisable BECAUSE it is
                             their colour, and filled-control-needs-a-ground
                             identifies these three by brand hex. Reverted. */
                          style={{ backgroundColor: '#16A34A', color: 'white' }}
                          data-testid="free-checkout-btn"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Activating…
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              Complete Free Order
                            </>
                          )}
                        </button>
                        <p
                          className="text-sm text-center mt-3"
                          style={{ color: 'var(--ink-2)' }}
                        >
                          Your promo covers the full amount — no payment required.
                        </p>
                      </div>
                    ) : gateway === 'razorpay' ? (
                      /* Indian users — Razorpay */
                      <div>
                        {user.email && (
                          <p
                            className="text-sm text-center mb-4"
                            style={{ color: 'var(--ink-2)' }}
                          >
                            Paying as{' '}
                            <span className="font-medium" style={{ color: 'var(--ink-2)' }}>
                              {user.email}
                            </span>
                          </p>
                        )}
                        <button
                          onClick={handleRazorpay}
                          disabled={loading}
                          className="w-full py-3.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-60"
                          style={{
                            /* Razorpay's own brand fill. Same reasoning as the
                               gateway button above - this one even carries
                               their logo. Not ours to restyle. */
                            backgroundColor: '#2D2D2D',
                            color: 'white',
                          }}
                          data-testid="razorpay-pay-btn"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Opening Razorpay…
                            </>
                          ) : (
                            <>
                              {/* Razorpay logo */}
                              <svg className="w-4 h-4" viewBox="0 0 30 30" fill="currentColor" aria-hidden="true">
                                <path d="M14.983 0L8.411 19.371l6.177-3.59 1.583 10.22L28.002 5.36l-7.029 3.84z" />
                              </svg>
                              Pay ₹{discountedTotalInr} with Razorpay
                            </>
                          )}
                        </button>
                        <p
                          className="text-sm text-center mt-3 flex items-center justify-center gap-1"
                          style={{ color: 'var(--ink-2)' }}
                        >
                          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                          Secured by Razorpay · UPI, Cards, NetBanking accepted
                        </p>
                      </div>
                    ) : (
                      /* International users — PayPal */
                      <div>
                        {user.email && (
                          <p
                            className="text-sm text-center mb-4"
                            style={{ color: 'var(--ink-2)' }}
                          >
                            Paying as{' '}
                            <span className="font-medium" style={{ color: 'var(--ink-2)' }}>
                              {user.email}
                            </span>
                          </p>
                        )}
                        <button
                          onClick={handlePayPal}
                          disabled={loading}
                          className="w-full py-3.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-60"
                          style={{
                            backgroundColor: '#0070BA',
                            color: 'white',
                          }}
                          data-testid="paypal-pay-btn"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Redirecting to PayPal…
                            </>
                          ) : (
                            <>
                              {/* PayPal "P" logo */}
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.59 3.025-2.566 6.082-8.558 6.082h-2.19a2.137 2.137 0 0 0-2.1 1.8l-1.12 7.107-.31 1.964H9.03c.459 0 .85-.333.92-.787l.038-.197.732-4.64.047-.254a.932.932 0 0 1 .92-.789h.579c3.75 0 6.686-1.523 7.545-5.927.358-1.839.173-3.373-.589-4.073z" />
                              </svg>
                              Pay ${fmtUsd(discountedTotalUsd)} with PayPal
                            </>
                          )}
                        </button>
                        <p
                          className="text-sm text-center mt-3 flex items-center justify-center gap-1"
                          style={{ color: 'var(--ink-2)' }}
                        >
                          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                          Secured by PayPal. You'll be redirected to complete payment.
                        </p>
                      </div>
                    )}

                    <button
                      onClick={() => { setStep('addons'); setError(''); }}
                      className="w-full mt-4 py-2 text-sm font-medium text-center transition-opacity hover:opacity-60 flex items-center justify-center gap-1"
                      style={{ color: 'var(--ink-2)' }}
                    >
                      ← Back to add-ons
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </GoogleOAuthProvider>
  );
}
